import * as Sentry from '@sentry/browser'
import { createContext, Dispatch, ReactNode, SetStateAction, useEffect, useState } from 'react'

import i18n from '@/locale/i18n'
import { Country } from '@/types/country'
import { User } from '@/types/user'

export interface UserContextProps {
  user: User | null
  setUser: Dispatch<SetStateAction<User | null>>
}

export const UserContext = createContext({} as UserContextProps)

interface UserProviderProps {
  children: ReactNode
}

export const UserProvider = ({ children }: UserProviderProps) => {
  const [user, setUser] = useState<UserContextProps['user']>(null)

  useEffect(() => {
    if (!user) return

    Sentry.setUser({
      uniqueId: user?.data?.uniqueId,
      userId: user?.data?.userId
    })

    Sentry.setTags({
      uniqueId: user?.data?.uniqueId,
      userId: user?.data?.userId
    })

    Sentry.setContext('User route conditions', {
      ...user.routeConditions
    })

    localStorage.setItem('user', JSON.stringify(user))
  }, [user])

  useEffect(() => {
    const lsUser = localStorage.getItem('user')

    const userObj: User = lsUser ? JSON.parse(lsUser) : {}

    const consultationType = new URLSearchParams(window.location.search).get('q') as string

    const language =
      userObj?.data?.language || localStorage.getItem('languageSelected') || new URLSearchParams(window.location.search).get('language') || undefined
    if (language && language !== i18n.language) i18n.changeLanguage(language)

    const country = new URLSearchParams(window.location.search).get('country') || undefined
    const countryObj = {
      code: country
    } as Country

    setUser({
      ...userObj,
      data: {
        ...userObj.data,
        language,
        country: userObj?.data?.country || (country ? countryObj : undefined)
      },
      routeConditions: {
        ...userObj.routeConditions,
        consultationType
      }
    })
  }, [])

  const value = {
    user,
    setUser
  }

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}
